import {omitBy, sortBy} from "lodash";
import {safeJSONParse} from '@/utils';
import {getCountryJson} from "@/apis/payment";
import {isINTaxExempt} from '@/utils/tax';
import {
  checkABNNumber,
  checkAEBNNumber,
  checkIDBNNumber,
  checkKRBNNumber,
  checkMXBNNumber,
  checkSABNNumber,
  checkSGBNNumber,
  checkZABNNumber,
  checkINTaxData,
} from '@/utils/tax-number-validation';

export const RESTRICTED_COUNTRIES = ['CU', 'IR', 'KP', 'SY', 'CN'];

export const ADDRESS_ACTION_TYPES = {
  fetchCountryMap: '/get/country/map',
}

export const CONTACT_TYPES = {
  BILLING_TO: 0,
  SOLD_TO: 1
}

export const TAX_ID_STATUS = {
  UNVERIFIED: 'unverified',
  VERIFIED: 'verified',
  FAILED: 'failed',
  STATE_MISMATCH: 'state_mismatch'
}

export const TAX_PREFIX = {
  'GR': 'EL',
  'MC': 'FR',
  'IM': 'GB'
}

export const NO_PREFIX_COUNTRY = ['GR', 'LI', 'MC', 'IM', 'SG', 'AU', 'RU', 'IN'];

// Used for country like 'Monaco'
export const COUNTRY_REPLACEMENT = {
  'MC': 'FR',
  'IM': 'GB',
  'IC': 'ES'
};

export const TAX_TYPE = {
  INPUT: 'input',
  DROPDOWN: 'dropdown'
};

export const taxConfigurationGetter = $t => ({
  'PE_HOLD_ON': {
    message: $t('billing.vat.pe_msg'),
    placeholder: 'RUC',
    checkbox: $t('billing.vat.pe_checkbox'),
    tooltip: $t('billing.vat.pe_tooltip'),
    validation: /^(10|20)\d{9}$/,
    isEmptiable: true,
    revert: true,
  },
  'AM': {
    message: $t('billing.amMsg'),
    placeholder: $t('billing.vat_registration_placeholder'),
    checkbox: $t('billing.registered_for_vat'),
    tooltip: $t('billing.amMsgDesc'),
    validation: /^\d{8}\/1$/,
    revert: true,
    isEmptiable: true,
    ignorePrefix: true
  },
  'AU': {
    message: $t('billing.not_gst_tips_for_au'),
    prefix: 'GST',
    placeholder: $t('billing.gst_number'),
    checkbox: $t('billing.not_gst_registered'),
    tooltip: $t('billing.gst_checkbox_desc'),
    validation: checkABNNumber,
    ignorePrefix: true
  },
  'BS': {
    message: $t('billing.vat_header_val_BS'),
    placeholder: 'TIN',
    validation: /^\d{9}$/,
    ignorePrefix: true,
    isEmptiable: true
  },
  'BH': {
    message: $t('billing.vat_header_val_BH'),
    placeholder: $t('billing.vat_id_number_placeholder'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message_IS_BH'),
    validation: /^\d{15}$/
  },
  'BD': {
    message: $t('billing.vat_header_val_BD'),
    placeholder: 'BIN',
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message_BD'),
  },
  'BB': {
    message: $t('billing.vat_header_val_BB'),
    placeholder: $t('billing.vat_number'),
    validation: /^\d{13}$/,
    taxExempt: false,
    isEmptiable: true
  },
  'SN': {
    message: $t('billing.vat_SN_message'),
    placeholder: $t('billing.vat_SN_placeholder'),
    validation: /^[0-9]{9} ?[012][a-zA-Z][0-9]$/,
    isEmptiable: true
  },
  'BY': {
    message: $t('billing.vat.msg_by'),
    placeholder: $t('billing.tin_number'),
    checkbox: $t('billing.vat.checkbox_by'),
    tooltip: $t('billing.vat.tooltip_by'),
    validation: /^\d{9}$/,
  },
  'CO': {
    message: $t('billing.vat.msg_co'),
    placeholder: $t('billing.vat.placeholder_co'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat.tooltip_co'),
    validation: /^\d{9}-\d$/
  },
  'KH': {
    message: $t('billing.vat_header_val_KHM'),
    placeholder: 'TIN',
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message_KHM'),
    validation: /^[LlKk]00\d-\d{9}$|^[Ee]00\d-\d{10}$/
  },
  'CM': {
    message: $t('billing.vat_header_val_CM'),
    placeholder: 'NIU',
    taxExempt: false,
    isEmptiable: true
  },
  'CL': {
    message: $t('billing.tax_promote_cl'),
    placeholder: $t('billing.rut_number'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat.tooltip_cl'),
    validation: /^(\w{9})$/,
    ignore:/-|\s|\./g,
  },
  'EG': {
    message: $t('billing.vat.tax_promote_eg'),
    placeholder: $t('billing.vat.tax_registration_number'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat.tooltip_eg'),
    validation: /^\d{3}-\d{3}-\d{3}$|^\d{3}\/\d{3}\/\d{3}$|^\d{9}$/,
    uinValidation: /^[A-Za-z0-9]{39}$/,
  },
  'GE': {
    message: $t('billing.vat_header_val_GE'),
    placeholder: $t('billing.vat_number'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message', [$t('billing.vat_checkout_popup_message_GE')]),
    validation: /^\d{9}$/
  },
  'GH': {
    message: $t('billing.vat_header_val_GH'),
    placeholder: 'TIN',
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message_IS_BH'),
    validation: /^[CV]\d{10}$/
  },
  'IS': {
    message: $t('billing.vat_header_val_IS'),
    placeholder: $t('billing.vat_id_number_placeholder'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message_IS_BH'),
    validation: /^\d{5}$|^\d{6}$/
  },
  'IN': {
    message: $t('billing.inMsg'),
    placeholder: $t('billing.gst_number'),
    tooltip: $t('billing.gst_checkbox_desc_template', [$t('billing.tax_checkbox_desc_non_inr_in')]),
    validation: /^\d{2}[A-Za-z0-9]{13}$/,
    taxExempt: isINTaxExempt,
    taxDataValidation: checkINTaxData,
    isEmptiable: true,
    revert: true
  },
  'ID': {
    message: $t('billing.tax_promote_vat_cat1'),
    placeholder: $t('billing.vat_number'),
    validation: checkIDBNNumber,
    isEmptiable: true
  },
  'JP': {
    message: $t('billing.tax_number_on_invoice_tips'),
    placeholder: 'JCT Number (Optional)',
    isEmptiable: true
  },
  'KZ': {
    message: $t('billing.vat_header_val_KAZ'),
    placeholder: $t('billing.vat_number'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message', [$t('billing.vat_checkout_popup_message_KAZ')]),
    validation: /^\d{12}$/
  },
  'KE': {
    message: $t('billing.keMsg'),
    placeholder: $t('billing.pin_number'),
    validation: /^[AP][0-9]{9}[A-Z]$/,
    isEmptiable: true
  },
  'KR': {
    message: $t('billing.vat.msg_kr'),
    prefix: 'KR',
    placeholder: $t('billing.vat_number'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_cat2_checkbox_desc'),
    validation: checkKRBNNumber
  },
  'LI': {
    message: $t('billing.tax_promote_vat_cat1'),
    prefix: 'MWST',
    placeholder: $t('billing.vat_number'),
    validation: /^\d{5}$/,
    isEmptiable: true
  },
  'MY': {
    message: $t('billing.tax_promote_my'),
    suffix: 'SST',
    placeholder: 'SST Number',
    isEmptiable: true
  },
  'MX': {
    message: $t('billing.tax_promote_vat_cat1'),
    placeholder: $t('billing.vat_number'),
    validation: checkMXBNNumber,
    isEmptiable: true
  },
  'MD': {
    message: $t('billing.tax_number_promote.default'),
    placeholder: 'TIN',
    checkbox: $t('billing.tax_checkbox_md'),
    tooltip: $t('billing.mdMsgDesc'),
    validation: /^1\d{12}$/
  },
  'NG': {
    message: $t('billing.ngMsg'),
    placeholder: $t('billing.tin_number'),
    validation: /^[0-9]{8}-[0-9]{4}$/,
    isEmptiable: true
  },
  'NO': {
    message: $t('billing.tax_promote_no'),
    placeholder: $t('billing.mva_number'),
    suffix: 'MVA',
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.tax_checkbox_desc_template', [$t('billing.tax_checkbox_desc_no')]),
    validation: /^\d{9}$/
  },
  'OM': {
    message: $t('billing.vat_header_val_OM'),
    placeholder: $t('billing.vat_number'),
    prefix: 'OM',
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message', [$t('billing.vat_checkout_popup_message_OM')]),
    validation: /^\d{10}$/
  },
  'RU': {
    message: $t('billing.tax_promote.include_your_vat_number'),
    placeholder: $t('billing.vat_number'),
    prefix: 'RU',
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message_RU')
  },
  'SA': {
    message: $t('billing.tax_promote_default'),
    placeholder: $t('billing.vat_number'),
    prefix: 'SA',
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_cat2_checkbox_desc'),
    validation: checkSABNNumber
  },
  'RS': {
    message: $t('billing.vat_header_val_RS'),
    placeholder: $t('billing.vat_number'),
    validation: /^\d{9}$/,
    taxExempt: false,
    isEmptiable: true
  },
  'SG': {
    message: $t('billing.not_gst_tips'),
    placeholder: $t('billing.gst_number'),
    prefix: 'GST',
    checkbox: $t('billing.not_gst_registered'),
    tooltip: $t('billing.gst_checkbox_desc'),
    validation: checkSGBNNumber
  },
  'ZA': {
    message: $t('billing.tax_promote_vat_cat1'),
    placeholder: $t('billing.vat_number'),
    validation: checkZABNNumber,
    isEmptiable: true
  },
  'CH': {
    message: $t('billing.tax_promote_vat_cat1'),
    placeholder: $t('billing.vat_number'),
    validation: window.checkVATNumber,
    isEmptiable: true
  },
  'TW': {
    message: $t('billing.vat_header_val_TW'),
    placeholder: $t('billing.vat_number'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message_TW'),
    validation: /^\d{8}$/
  },
  'TZ': {
    message: $t('billing.vat_header_val_TZ'),
    placeholder: 'VRN',
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message_BD'),
    validation: /^\d{2}-\d{6}-[A-Z]$/
  },
  'TH': {
    message: $t('billing.vat.msg_th'),
    placeholder: $t('billing.vat_number'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_cat2_checkbox_desc'),
    validation: /^\d{13}$/
  },
  'TR': {
    message: $t('billing.tax_promote_tr'),
    placeholder: $t('billing.tax_input_placeholder'),
    prefix: 'VAT',
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.tax_checkbox_desc_template', [$t('billing.tax_checkbox_desc_tr')]),
    validation: /^\d{10,11}$/
  },
  'UG': {
    message: $t('billing.ugMsg'),
    placeholder: 'TIN',
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message_KHM'),
    validation: /^\d{10}$/,
  },
  'UA': {
    message: $t('billing.vat_header_val_UKR'),
    placeholder: $t('billing.vat_number'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message', [$t('billing.vat_checkout_popup_message_UKR')]),
    validation: /^\d{9}$|^\d{10}$|^\d{12}$/
  },
  'UZ': {
    message: $t('billing.tax_promote_tr'),
    placeholder: $t('billing.vat_number'),
    checkbox: $t('billing.tax_checkbox_by'),
    tooltip: $t('billing.uzCheckboxMsg'),
    validation: /^\d{12}$/,
    isEmptiable: true,
    revert: true,
  },
  'AE': {
    message: $t('billing.tax_promote_vat_cat1'),
    placeholder: $t('billing.vat_number'),
    validation: checkAEBNNumber,
    isEmptiable: true
  },
  'VN': {
    message: $t('billing.vat_header_val_VNM'),
    placeholder: $t('billing.tax_input_placeholder'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message_VNM'),
    validation: /^\d{10}$/
  },
  'GB': {
    message: 'If you are a Business customer, enter VAT number',
    tooltip: 'In completing the checkbox I/we certify that the above named business is not VAT registered and that I/we take full responsibility for the accuracy and completeness of this statement. Zoom waives any responsibility to accept liability due to either an incomplete or an inaccurate VAT registration disclosure. Furthermore, in certifying that the above named Business is not VAT registered I/we accept and understand that Zoom will charge VAT on all taxable services at the applicable rate in accordance to UK legislation in addition to the prices listed in Order Summary.',
    validation: window.checkVATNumber
  },
  'IC': {
    message: $t('billing.vat.msg_ic'),
    placeholder: 'NIF',
    prefix: 'ES',
    tooltip: $t('billing.vat.tooltip_ic'),
    validation: window.checkVATNumber
  }
});

export const EU_DISCLAIM_COUNTRIES = ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL','PT', 'RO', 'SK', 'SI', 'ES', 'SE','GB', 'IS', 'LI', 'NO', 'DE','IM']
export const EU_COUNTRIES = ['AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'SE', 'MC', 'IM', 'GB', 'IC'];
export const UIN_NEEDED_COUNTRIES = ['EG'];

export const ONLINE_VALIDATION_COUNTRIES = ['BD', 'CM', 'AU', 'UG', 'TW', 'BH', 'IS', 'RU', 'VN', 'SG', 'BY', 'CL', 'CO', 'NO', 'SA', 'OM', 'TH', 'IN', 'KR', 'EG', 'PE'];

export const getDefaultAddressInfo = () => {
  return {
    address1: '',
    address2: '',
    zip: '',
    city: '',
    state: '',
    country: '',
    countryId: '',
    phone: '',
    accountHq: '',
    companyName: '',
    employeeCount: '',
    usageType: '',
    countryName: '',
    vatNumber: '',
    notVatRegistered: false,
    doingBusinessAsName: '',
    businessType: '',
    otherBusinessType: '',
  }
}

const state = {
  addressInfo: getDefaultAddressInfo(),
  countryMap: {},
  ipLocation: safeJSONParse(window.ipLocation || '{}', {}),
  useCaseList: []
}

const getters = {
  addressInfo: (state) => state.addressInfo,
  countryMap: (state) => state.countryMap,
  useCaseList: (state) => state.useCaseList,
  countryList: state => {
    const countryMap = state.countryMap;
    return sortBy(Object.keys(countryMap)).reduce((result, label) => {
      result.push({label: label, value: countryMap[label]});
      return result;
    }, [])
  },
}

const mutations = {
  setCountryMap(state, countryMap) {
    state.countryMap = countryMap;
  },
  setAddressInfo(state, data) {
    state.addressInfo = data;
  },
  setUsageType(state,data) {
    state.addressInfo.usageType =  data
  },
  setUseCaseList(state,data) {
    state.useCaseList=  data
  },
}

const actions = {
  async [ADDRESS_ACTION_TYPES.fetchCountryMap]({commit}) {
    try {
      let data = await getCountryJson();
      // data's format is {countryName:countryId}, like {china: cn}
      // need do filter, restrict some forbidden countries
      data = omitBy(data, function (value){
        return RESTRICTED_COUNTRIES.includes(value);
      })
      commit('setCountryMap', data);
    } catch (e) {
      console.error('get country map error');
    }
  },
  updateAddressInfo({commit}, {key, value}) {
    commit('setAddressInfo', {[key]: value});
  },
}

export default {
  state,
  getters,
  mutations,
  actions,
}
